/**
 * SectionRegistryPage — workspace-wide section type library.
 * Mounted inside SettingsShell as the "Section registry" tab.
 *
 * Admins / dept heads / PMs can:
 *   - Browse all registry entries (code, name, keywords, dept, required flag)
 *   - Create new entries
 *   - Edit any entry they created (or any entry if admin)
 *   - Delete their own entries (or any entry if admin)
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ── helpers ───────────────────────────────────────────────────────────────────

function toSlug(s) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function KeywordTokens({ keywords, editable, onChange }) {
  const [draft, setDraft] = useState("");

  function addToken(raw) {
    const token = raw.trim().toLowerCase();
    if (!token) return;
    if (!keywords.includes(token)) onChange([...keywords, token]);
    setDraft("");
  }

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {keywords.map((kw) => (
        <Badge key={kw} variant="secondary" className="text-xs gap-1 pr-1">
          {kw}
          {editable && (
            <button
              onClick={() => onChange(keywords.filter((k) => k !== kw))}
              className="hover:text-red-600 ml-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </Badge>
      ))}
      {editable && (
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (["Enter", ",", "Tab"].includes(e.key)) {
              e.preventDefault();
              addToken(draft);
            }
          }}
          onBlur={() => addToken(draft)}
          placeholder={keywords.length === 0 ? "Type keyword, press Enter…" : "+"}
          className="text-xs border border-dashed border-slate-300 rounded px-1.5 py-0.5 w-32 focus:outline-none focus:border-blue-400 bg-transparent"
        />
      )}
    </div>
  );
}

// ── form ──────────────────────────────────────────────────────────────────────

const EMPTY = {
  code: "",
  name: "",
  description: "",
  keyword_patterns: [],
  dept_id: "",
  is_required: false,
};

function RegistryForm({ initial, departments, loading, onSave, onCancel }) {
  const [form, setForm] = useState(initial ?? EMPTY);
  const codeAutoFilled = useRef(false);

  function set(key, val) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  function handleNameChange(val) {
    set("name", val);
    if (!codeAutoFilled.current || form.code === "") {
      set("code", toSlug(val));
    }
  }

  const valid = form.name.trim() && form.code.trim();

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Name *</label>
          <Input
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g. Earnest Money Deposit"
            className="h-8 text-sm mt-0.5"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Code *</label>
          <Input
            value={form.code}
            onChange={(e) => {
              codeAutoFilled.current = true;
              set("code", toSlug(e.target.value));
            }}
            placeholder="e.g. emd"
            className="h-8 text-sm mt-0.5 font-mono"
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Description</label>
        <Input
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Optional — shown when PMs browse the registry"
          className="h-8 text-sm mt-0.5"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Department</label>
        <Select
          value={form.dept_id || "__none__"}
          onValueChange={(v) => set("dept_id", v === "__none__" ? "" : v)}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue placeholder="No department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No department</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">
          Keywords (press Enter to add)
        </label>
        <KeywordTokens
          keywords={form.keyword_patterns}
          editable
          onChange={(kw) => set("keyword_patterns", kw)}
        />
      </div>
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={form.is_required}
          onChange={(e) => set("is_required", e.target.checked)}
          className="rounded"
        />
        <span className="text-xs text-slate-700">
          Required — warn when this section is missing from an analysed RFP
        </span>
      </label>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" disabled={!valid || loading} onClick={() => onSave(form)}>
          {loading ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

// ── entry row ─────────────────────────────────────────────────────────────────

function RegistryRow({ entry, departments, onEdit, onDelete }) {
  const dept = departments.find((d) => d.id === entry.dept_id);
  return (
    <div className="flex items-start justify-between gap-4 p-3 rounded-lg border bg-white hover:border-slate-300 transition-colors">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-slate-900">{entry.name}</span>
          <span className="font-mono text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
            {entry.code}
          </span>
          {dept && (
            <Badge variant="outline" className="text-xs">{dept.name}</Badge>
          )}
          {entry.is_required && (
            <Badge className="text-xs bg-amber-50 text-amber-700 border-amber-200 gap-1">
              <Star className="w-3 h-3" />
              Required
            </Badge>
          )}
        </div>
        {entry.description && (
          <p className="text-xs text-slate-500">{entry.description}</p>
        )}
        <div className="flex flex-wrap gap-1">
          {(entry.keyword_patterns ?? []).map((kw) => (
            <Badge key={kw} variant="secondary" className="text-xs">{kw}</Badge>
          ))}
          {(entry.keyword_patterns ?? []).length === 0 && (
            <span className="text-xs text-slate-400 italic">No keywords</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(entry)}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
          onClick={() => onDelete(entry)}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export default function SectionRegistryPage() {
  const [entries, setEntries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    Promise.all([v2Api.sectionRegistry.list(), v2Api.departments()])
      .then(([reg, depts]) => {
        setEntries(reg);
        setDepartments(depts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = entries.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      e.code.toLowerCase().includes(q) ||
      (e.description ?? "").toLowerCase().includes(q) ||
      (e.keyword_patterns ?? []).some((k) => k.includes(q))
    );
  });

  async function handleSave(form) {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description?.trim() || null,
        keyword_patterns: form.keyword_patterns,
        dept_id: form.dept_id || null,
        is_required: form.is_required,
      };
      if (editingEntry) {
        const updated = await v2Api.sectionRegistry.patch(editingEntry.id, payload);
        setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      } else {
        const created = await v2Api.sectionRegistry.create(payload);
        setEntries((prev) => [...prev, created]);
      }
      setDialogOpen(false);
      setEditingEntry(null);
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to save entry.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(entry) {
    if (!await confirm(`Delete registry entry "${entry.name}"?`, { title: "Delete registry entry", confirmLabel: "Delete" })) return;
    try {
      await v2Api.sectionRegistry.remove(entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to delete entry.");
    }
  }

  function openEdit(entry) {
    setEditingEntry(entry);
    setDialogOpen(true);
  }

  function openCreate() {
    setEditingEntry(null);
    setDialogOpen(true);
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4 pb-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">
            Workspace-wide library of section types. PMs pick from this list
            when configuring a project. Keywords drive the classifier.
          </p>
        </div>
        <Button size="sm" onClick={openCreate} className="shrink-0">
          <Plus className="w-4 h-4 mr-1" />
          New entry
        </Button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 flex justify-between">
          {error}
          <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, code or keyword…"
          className="pl-8 h-8 text-sm"
        />
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed rounded-lg p-10 text-center text-slate-400 text-sm">
          {entries.length === 0
            ? "No registry entries yet. Click \"New entry\" to add the first one."
            : "No entries match your search."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((entry) => (
            <RegistryRow
              key={entry.id}
              entry={entry}
              departments={departments}
              onEdit={openEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(v) => {
          if (!v) { setDialogOpen(false); setEditingEntry(null); setError(null); }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? "Edit registry entry" : "New registry entry"}
            </DialogTitle>
          </DialogHeader>
          <RegistryForm
            initial={
              editingEntry
                ? {
                    code: editingEntry.code,
                    name: editingEntry.name,
                    description: editingEntry.description ?? "",
                    keyword_patterns: editingEntry.keyword_patterns ?? [],
                    dept_id: editingEntry.dept_id ?? "",
                    is_required: editingEntry.is_required,
                  }
                : undefined
            }
            departments={departments}
            loading={saving}
            onSave={handleSave}
            onCancel={() => { setDialogOpen(false); setEditingEntry(null); }}
          />
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
